import { countries } from '@/mocks/countries';
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Countries() {
  const [selectedRegion, setSelectedRegion] = useState('All');

  const regions = ['All', 'Africa', 'Middle East', 'Asia', 'Europe/Asia'];
  const filtered = selectedRegion === 'All' ? countries : countries.filter((c) => c.region === selectedRegion);

  const regionCounts: Record<string, number> = {};
  countries.forEach((c) => {
    regionCounts[c.region] = (regionCounts[c.region] || 0) + 1;
  });

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20world%20map%20with%20navy%20blue%20and%20orange%20color%20scheme%2C%20elegant%20travel%20themed%20background%20with%20flag%20motifs%20and%20dotted%20flight%20routes%2C%20clean%20modern%20corporate%20design%2C%20subtle%20geometric%20patterns&width=1920&height=600&seq=countries-premium-visa&orientation=landscape"
          alt="Countries we assist with visa applications"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Countries We Assist With - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Countries We Assist With</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[600px] mx-auto">
            We provide professional visa application support for over 30 destinations worldwide
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              Select Your Destination Country
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-[650px] mx-auto">
              Browse the full list of countries where we can assist with your visa application. Click Apply Now to get started.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {regions.map((region) => (
              <button
                key={region}
                onClick={() => setSelectedRegion(region)}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedRegion === region
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                }`}
              >
                {region} {region !== 'All' && `(${regionCounts[region] || 0})`}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filtered.map((country) => (
              <div
                key={country.code}
                className="flex flex-col bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6 hover:border-primary-300 transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="w-[52px] h-[34px] flex items-center justify-center rounded-sm overflow-hidden bg-secondary-100 border border-secondary-200">
                    <img
                      src={`https://flagcdn.com/w160/${country.code.toLowerCase()}.png`}
                      alt={`Flag of ${country.name}`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      style={{ imageRendering: 'auto' }}
                    />
                  </div>
                </div>

                <h3 className="text-base md:text-lg font-semibold text-foreground-900 text-center mb-2 group-hover:text-primary-600 transition-colors">
                  {country.name}
                </h3>

                <div className="flex flex-wrap items-center justify-center gap-1 mb-4">
                  {country.services.slice(0, 2).map((service) => (
                    <span
                      key={service}
                      className="inline-block bg-secondary-100 text-foreground-600 text-[0.625rem] md:text-[0.6875rem] px-2 py-0.5 rounded-full whitespace-nowrap"
                    >
                      {service}
                    </span>
                  ))}
                  {country.services.length > 2 && (
                    <span className="inline-block bg-secondary-100 text-foreground-500 text-[0.625rem] md:text-[0.6875rem] px-2 py-0.5 rounded-full whitespace-nowrap">
                      +{country.services.length - 2}
                    </span>
                  )}
                </div>

                <div className="mt-auto flex flex-col gap-2">
                  <Link
                    to="/apply-now"
                    className="inline-block w-full px-6 py-2 bg-primary-500 text-background-50 text-xs font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap text-center cursor-pointer"
                  >
                    Apply Now
                  </Link>
                  <Link
                    to="/apply-from-home"
                    className="inline-block w-full px-6 py-2 bg-accent-500 text-background-50 text-xs font-medium rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap text-center cursor-pointer"
                  >
                    Get Visa From Home
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {selectedRegion === 'All' && (
            <p className="text-center text-sm text-foreground-500 mt-8 mb-4">
              Showing all {filtered.length} countries
            </p>
          )}
        </div>
      </section>

      <section className="py-14 md:py-20 bg-secondary-100">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
            Don&apos;t See Your Destination?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-[600px] mx-auto mb-6">
            If your destination country is not listed above, please contact our team. We may still be able to assist with your visa application.
          </p>
          <Link
            to="/contact-us"
            className="inline-block px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
          >
            Contact Our Team
          </Link>
        </div>
      </section>
    </div>
  );
}